import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import { useWebSocket } from "../../../contexts/WebSocketContext";
import { formatDate } from "../../../utils/dateUtils";
import { Plus, Minus, Loader2 } from "lucide-react";
import { getNotificationCardBg, getNotificationCardBorder, getNotificationCardHeading, getNotificationCardText } from "../../../utils/statusColors";

interface Seat {
  _id: string;
  userId?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
  };
  userEmail?: string;
  status: "active" | "suspended" | "pending";
  assignedAt?: string;
  createdAt: string;
}

interface Subscription {
  totalSeats: number;
  usedSeats: number;
  plan: "free" | "premium";
  status: "active" | "past_due" | "canceled" | "incomplete" | "trialing";
}

export const SeatManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { socket } = useWebSocket();
  const [seats, setSeats] = useState<Seat[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState<Seat | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [updatingSeats, setUpdatingSeats] = useState(false);
  const [newSeatCount, setNewSeatCount] = useState<number>(1);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchSeats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const [seatsResponse, subscriptionResponse] = await Promise.all([
        fetch(`${apiUrl}/api/seats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${apiUrl}/api/subscriptions/current`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const seatsData = await seatsResponse.json();
      const subscriptionData = await subscriptionResponse.json();

      if (seatsData.success) {
        setSeats(seatsData.data);
      } else {
        setError(seatsData.message);
      }

      if (subscriptionData.success && subscriptionData.data?.subscription) {
        const sub = subscriptionData.data.subscription;
        setSubscription({
          totalSeats: sub.totalSeats,
          usedSeats: sub.usedSeats,
          plan: sub.plan,
          status: sub.status || "active",
        });
        setNewSeatCount(sub.totalSeats);
      }
    } catch (err) {
      console.error("Fetch seats error:", err);
      setError(t("admin.failedToLoadSeats"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeats();
  }, []);

  // Listen for real-time subscription updates via Socket.IO
  useEffect(() => {
    if (!socket) return;

    const handleSubscriptionUpdate = (data: {
      plan: "free" | "premium";
      status: "active" | "past_due" | "canceled" | "incomplete" | "trialing";
      totalSeats: number;
      usedSeats: number;
      timestamp: string;
    }) => {
      console.log(
        "📡 Received subscription:updated event in SeatManagement:",
        data,
      );
      // Update subscription data
      setSubscription({
        totalSeats: data.totalSeats,
        usedSeats: data.usedSeats,
        plan: data.plan,
        status: data.status,
      });
      setNewSeatCount(data.totalSeats);
      // Refresh seats list
      fetchSeats();
    };

    socket.on("subscription:updated", handleSubscriptionUpdate);

    return () => {
      socket.off("subscription:updated", handleSubscriptionUpdate);
    };
  }, [socket]);

  const handleAssignSeat = async () => {
    if (!selectedSeat || !userEmail) {
      alert(t("admin.pleaseEnterUserEmail"));
      return;
    }

    setAssigning(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/seats/assign`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          seatId: selectedSeat._id,
          userEmail,
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert(t("admin.seatAssignedSuccessfully"));
        setShowAssignModal(false);
        setUserEmail("");
        setSelectedSeat(null);
        fetchSeats();
      } else {
        alert(data.message || t("admin.failedToAssignSeat"));
      }
    } catch (err) {
      console.error("Assign seat error:", err);
      alert(t("admin.failedToAssignSeat"));
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignSeat = async (seatId: string) => {
    if (!window.confirm(t("admin.confirmUnassignSeat"))) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/seats/${seatId}/unassign`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        alert(t("admin.seatUnassignedSuccessfully"));
        fetchSeats();
      } else {
        alert(data.message || t("admin.failedToUnassignSeat"));
      }
    } catch (err) {
      console.error("Unassign seat error:", err);
      alert(t("admin.failedToUnassignSeat"));
    }
  };

  const handleSuspendSeat = async (seatId: string) => {
    if (!window.confirm(t("admin.confirmSuspendSeat"))) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/seats/${seatId}/suspend`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        alert(t("admin.seatSuspendedSuccessfully"));
        fetchSeats();
      } else {
        alert(data.message || t("admin.failedToSuspendSeat"));
      }
    } catch (err) {
      console.error("Suspend seat error:", err);
      alert(t("admin.failedToSuspendSeat"));
    }
  };

  const handleActivateSeat = async (seatId: string) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/seats/${seatId}/activate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        alert(t("admin.seatActivatedSuccessfully"));
        fetchSeats();
      } else {
        alert(data.message || t("admin.failedToActivateSeat"));
      }
    } catch (err) {
      console.error("Activate seat error:", err);
      alert(t("admin.failedToActivateSeat"));
    }
  };

  const handleUpdateSeats = async () => {
    // Premium requires both plan=premium AND status=active (or trialing)
    const isPremium = subscription?.plan === "premium" && (subscription?.status === "active" || subscription?.status === "trialing");
    if (!subscription || !isPremium) {
      alert("Seat management is only available on active Premium plan");
      return;
    }

    if (newSeatCount < subscription.usedSeats) {
      alert(
        `Cannot reduce to ${newSeatCount} seats. You currently have ${subscription.usedSeats} assigned seats. Please unassign users first.`,
      );
      return;
    }

    if (newSeatCount === subscription.totalSeats) {
      return; // No change
    }

    setUpdatingSeats(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/subscriptions/update-seats`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ seats: newSeatCount }),
      });

      const data = await response.json();
      if (data.success) {
        alert(`Successfully updated seats to ${newSeatCount}`);
        fetchSeats();
      } else {
        alert(data.message || "Failed to update seats");
      }
    } catch (err) {
      console.error("Update seats error:", err);
      alert("Failed to update seats");
    } finally {
      setUpdatingSeats(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return t("admin.active");
      case "pending":
        return t("admin.pending");
      case "suspended":
        return t("admin.suspended");
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          {t("admin.loadingSeats")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-error-800 dark:text-error-200">{error}</p>
      </div>
    );
  }

  const activeSeats = seats.filter((s) => s.status === "active");
  const pendingSeats = seats.filter((s) => s.status === "pending");
  const suspendedSeats = seats.filter((s) => s.status === "suspended");

  // Premium requires both plan=premium AND status=active (or trialing)
  const isPremium = subscription?.plan === "premium" && (subscription?.status === "active" || subscription?.status === "trialing");
  const canModifySeats = isPremium && subscription;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t("admin.seatManagement")}
          </h2>
          {canModifySeats && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setNewSeatCount(Math.max(1, newSeatCount - 1))}
                  disabled={
                    updatingSeats || newSeatCount <= subscription.usedSeats
                  }
                  className="p-1 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min={subscription.usedSeats}
                  value={newSeatCount}
                  onChange={(e) => {
                    const val =
                      parseInt(e.target.value) || subscription.usedSeats;
                    setNewSeatCount(Math.max(subscription.usedSeats, val));
                  }}
                  className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={updatingSeats}
                />
                <button
                  onClick={() => setNewSeatCount(newSeatCount + 1)}
                  disabled={updatingSeats}
                  className="p-1 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleUpdateSeats}
                disabled={
                  updatingSeats || newSeatCount === subscription.totalSeats
                }
                className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white rounded-md hover:bg-accent-700 dark:hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updatingSeats ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Seats"
                )}
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`${getNotificationCardBg("info")} ${getNotificationCardBorder("info")} border rounded-lg p-4`}>
            <h3 className={`text-sm font-medium mb-1 ${getNotificationCardHeading("info")}`}>
              Total Seats
            </h3>
            <p className={`text-3xl font-bold ${getNotificationCardText("info")}`}>
              {subscription?.totalSeats || 0}
            </p>
          </div>
          <div className={`${getNotificationCardBg("success")} ${getNotificationCardBorder("success")} border rounded-lg p-4`}>
            <h3 className={`text-sm font-medium mb-1 ${getNotificationCardHeading("success")}`}>
              {t("admin.activeSeats")}
            </h3>
            <p className={`text-3xl font-bold ${getNotificationCardText("success")}`}>
              {activeSeats.length}
            </p>
          </div>
          <div className={`${getNotificationCardBg("warning")} ${getNotificationCardBorder("warning")} border rounded-lg p-4`}>
            <h3 className={`text-sm font-medium mb-1 ${getNotificationCardHeading("warning")}`}>
              {t("admin.pendingSeats")}
            </h3>
            <p className={`text-3xl font-bold ${getNotificationCardText("warning")}`}>
              {pendingSeats.length}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-300 dark:border-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t("admin.suspendedSeats")}
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {suspendedSeats.length}
            </p>
          </div>
        </div>
        {canModifySeats && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {subscription.usedSeats} of {subscription.totalSeats} seats are
              currently assigned.
              {newSeatCount !== subscription.totalSeats && (
                <span className="font-semibold ml-1">
                  {newSeatCount > subscription.totalSeats
                    ? `Adding ${newSeatCount - subscription.totalSeats} seat(s) will update your subscription.`
                    : `Removing ${subscription.totalSeats - newSeatCount} seat(s) will update your subscription.`}
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Seats Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("admin.allSeats")}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("admin.user")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("admin.status")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("admin.assignedDate")}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {seats.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                  >
                    {t("admin.noSeatsFound")}
                  </td>
                </tr>
              ) : (
                seats.map((seat) => (
                  <tr
                    key={seat._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {seat.userId ? (
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {seat.userId.profileImage ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={seat.userId.profileImage}
                                alt=""
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
                                <span className="text-accent-600 dark:text-accent-400 font-medium text-sm">
                                  {seat.userId.firstName[0]}
                                  {seat.userId.lastName[0]}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {seat.userId.firstName} {seat.userId.lastName}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {seat.userId.email}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {seat.userEmail || t("admin.unassigned")}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          seat.status === "active"
                            ? "bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300"
                            : seat.status === "pending"
                              ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                        }`}
                      >
                        {getStatusLabel(seat.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {seat.assignedAt ? formatDate(seat.assignedAt) : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {!seat.userId && seat.status === "pending" && (
                        <button
                          onClick={() => {
                            setSelectedSeat(seat);
                            setShowAssignModal(true);
                          }}
                          className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300"
                        >
                          {t("admin.assignSeat")}
                        </button>
                      )}
                      {seat.userId && seat.status === "active" && (
                        <>
                          <button
                            onClick={() => handleUnassignSeat(seat._id)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                          >
                            {t("admin.unassign")}
                          </button>
                          <button
                            onClick={() => handleSuspendSeat(seat._id)}
                            className="text-error-600 dark:text-error-400 hover:text-error-900 dark:hover:text-error-300"
                          >
                            {t("admin.suspend")}
                          </button>
                        </>
                      )}
                      {seat.status === "suspended" && (
                        <button
                          onClick={() => handleActivateSeat(seat._id)}
                          className="text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300"
                        >
                          {t("admin.activate")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && selectedSeat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t("admin.assignSeatTitle")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t("admin.assignSeatDescription")}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("admin.userEmail")}
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder={t("admin.userEmailPlaceholder")}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleAssignSeat}
                disabled={assigning}
                className="flex-1 px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white rounded-md hover:bg-accent-700 dark:hover:bg-accent-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 transition-colors"
              >
                {assigning ? t("admin.assigning") : t("admin.assignSeat")}
              </button>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setUserEmail("");
                  setSelectedSeat(null);
                }}
                disabled={assigning}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
